import { useState, useMemo } from "react";

const questions = [
  {
    id: 1,
    question: "A retail investor holds the following accounts with a CIRO-regulated dealer that suddenly becomes insolvent: $400,000 in a Cash account, $500,000 in a TFSA, $300,000 in a Margin account, $900,000 in an RRSP, and $200,000 in a Spousal RRSP. What is the exact total compensation the Canadian Investor Protection Fund (CIPF) will provide?",
    options: { A: "$1,000,000", B: "$2,300,000", C: "$1,200,000", D: "$2,000,000" },
    answer: "D",
    rationale: "CIPF calculates limits by combining specific categories. General Category: Cash ($400k) + TFSA ($500k) + Margin ($300k) = $1.2 Million — capped at $1,000,000. Retirement Category: RRSP ($900k) + Spousal RRSP ($200k) = $1.1 Million — also capped at $1,000,000. Total: $1M + $1M = $2,000,000."
  },
  {
    id: 2,
    question: "While CIRO regulates investment dealers, which organization acts as the prudential regulator ensuring the financial stability and capital adequacy of federally chartered banks and insurance companies?",
    options: { A: "The Office of the Superintendent of Financial Institutions (OSFI)", B: "The Canadian Securities Administrators (CSA)", C: "The Financial Consumer Agency of Canada (FCAC)", D: "The Department of Finance" },
    answer: "A",
    rationale: "OSFI is the federal watchdog for institutional health. They ensure that major banks like RBC or TD have enough capital reserves to survive severe economic recessions without failing — this is 'prudential' regulation, focused on the safety of the institution itself."
  },
  {
    id: 3,
    question: "Under CASL, an individual sends an email to an advisor asking for a quote on financial planning fees. The advisor provides the quote but the individual does not buy anything. How long does the advisor have 'implied consent' to send this prospect marketing emails?",
    options: { A: "12 months", B: "24 months", C: "6 months", D: "Until they explicitly unsubscribe" },
    answer: "C",
    rationale: "An inquiry (asking a question or requesting a quote) establishes a short-term relationship. CASL grants exactly 6 months of implied consent for an inquiry. If they had actually purchased a service, implied consent would mathematically extend to 24 months."
  },
  {
    id: 4,
    question: "What is the fundamental purpose of the Canadian Securities Administrators (CSA)?",
    options: { A: "To manage daily trading operations of the Toronto Stock Exchange", B: "To act as a centralized federal securities regulator in Ottawa", C: "To coordinate and harmonize capital markets regulation across the 13 independent provincial and territorial securities commissions", D: "To guarantee retail deposits against market loss" },
    answer: "C",
    rationale: "Canada is unique because it doesn't have a single national SEC like the US. Securities law is provincial. The CSA exists so provinces can work together to create 'National Instruments' (like NI 31-103), ensuring the rules in BC are almost identical to the rules in Nova Scotia."
  },
  {
    id: 5,
    question: "A client deposits $4,000 CAD in physical cash at 10:00 AM. At 3:00 PM the same day, they deposit $5,000 USD in physical cash. The exchange rate is 1 USD = 1.30 CAD. Under the PCMLTFA, what is the exact regulatory requirement?",
    options: { A: "File a Large Cash Transaction Report (LCTR) within 15 calendar days because the 24-hour CAD equivalent total exceeds $10,000", B: "File a Suspicious Transaction Report (STR) within 30 days", C: "No report is required because each currency individually was under $10,000", D: "The dealer must freeze the account immediately" },
    answer: "A",
    rationale: "The '24-Hour Rule' aggregates all physical cash deposits within a rolling 24-hour window. Convert: $5,000 USD × 1.30 = $6,500 CAD. Add: $6,500 + $4,000 = $10,500 CAD total. Because $10,500 ≥ $10,000, an LCTR must be filed within the strict 15-day deadline."
  },
  {
    id: 6,
    question: "If a retail client purchases newly issued mutual fund securities via a prospectus, Canadian securities law provides them with a statutory 'Right of Withdrawal.' What is the exact timeline for this right?",
    options: { A: "5 business days from the trade date", B: "30 calendar days from the settlement date", C: "2 business days after receipt of the prospectus", D: "24 hours from the order execution" },
    answer: "C",
    rationale: "The Right of Withdrawal is a mandatory cooling-off period. It allows an investor to completely cancel a new-issue purchase, no questions asked, within two business days of receiving the prospectus."
  },
  {
    id: 7,
    question: "Which electronic database must a corporate insider (e.g., a major shareholder owning 15% of a company's voting stock) use to publicly disclose their personal trades?",
    options: { A: "NRD (National Registration Database)", B: "SEDI (System for Electronic Disclosure by Insiders)", C: "SEDAR+ (System for Electronic Document Analysis and Retrieval)", D: "CIPF (Canadian Investor Protection Fund)" },
    answer: "B",
    rationale: "SEDI is the specific platform used to track insider trading. Insiders must log their trades here within 5 calendar days so the public can see if the CEO is secretly dumping or buying up their own company's stock."
  },
  {
    id: 8,
    question: "An Investment Dealer receives a formal written complaint from a client on May 1st. According to CIRO rules, the dealer must send a 'Substantive Response Letter' containing their final decision by no later than what threshold?",
    options: { A: "30 calendar days", B: "90 calendar days", C: "60 business days", D: "180 calendar days" },
    answer: "B",
    rationale: "The firm has exactly 90 calendar days from the day the complaint is received to complete its investigation and deliver its final, substantive response to the client."
  },
  {
    id: 9,
    question: "The Bank of Canada uses the overnight interest rate as its primary monetary policy tool. What is the Bank's specific numerical target for inflation?",
    options: { A: "A target range of 0% to 2%, aiming for 1%", B: "A target range of 2% to 4%, aiming for 3%", C: "A target range of 1% to 3%, specifically aiming for the 2% midpoint", D: "0% inflation (absolute price stability)" },
    answer: "C",
    rationale: "The Bank of Canada does not want zero inflation (which risks deflation). They aim to keep CPI inflation in the 1%–3% control range, actively adjusting interest rates to pin inflation directly on the 2% target."
  },
  {
    id: 10,
    question: "A client has $150,000 in a GIC, $50,000 in a high-interest savings account, and $100,000 in mutual funds, all held directly at a single federally regulated Canadian Bank. If the bank goes bankrupt, how much will CDIC cover?",
    options: { A: "$300,000", B: "$100,000", C: "$200,000", D: "$0" },
    answer: "B",
    rationale: "CDIC covers eligible deposits up to $100,000 per category, per institution. The GIC ($150k) and savings account ($50k) are grouped in the same individual category, capping coverage at $100k. Mutual funds are never covered by CDIC. Total payout = $100,000."
  },
  {
    id: 11,
    question: "Under the 'Passport System,' if an Investment Dealer headquartered in Alberta wants to register advisors in British Columbia and Saskatchewan, what must they do?",
    options: { A: "File completely separate applications with the BCSC and the FCAA", B: "Deal only with their principal regulator (the Alberta Securities Commission), which facilitates the registrations in BC and SK", C: "Apply directly to the Federal Department of Finance", D: "Take a specific provincial law exam for each new province" },
    answer: "B",
    rationale: "The Passport System eliminates duplicative red tape. A firm files paperwork with their 'home' (principal) regulator. If approved there, the other participating provinces automatically grant access."
  },
  {
    id: 12,
    question: "PIPEDA is built on 10 Fair Information Principles. Which principle states that a firm must designate a specific individual (such as a Privacy Officer) to oversee compliance with privacy legislation?",
    options: { A: "Safeguards", B: "Limiting Collection", C: "Accountability", D: "Identifying Purposes" },
    answer: "C",
    rationale: "'Accountability' ensures that privacy isn't just an abstract corporate concept. The law demands a specific human being is assigned the title and responsibility of protecting client data."
  },
  {
    id: 13,
    question: "A client receives a Substantive Response Letter from their dealer denying their complaint. How many days does the client have to escalate the matter to the Ombudsman for Banking Services and Investments (OBSI)?",
    options: { A: "30 days", B: "180 days", C: "90 days", D: "1 year" },
    answer: "B",
    rationale: "The dealer's final response letter must explicitly state that the client has exactly 180 calendar days to take the dispute to OBSI if they are unsatisfied with the firm's decision."
  },
  {
    id: 14,
    question: "An Approved Person is making telemarketing calls to prospect for new clients. They must scrub their call list against the National Do Not Call List (DNCL). How frequently must this occur?",
    options: { A: "Once every 31 days", B: "Once every 7 days", C: "Once a year", D: "Once every 90 days" },
    answer: "A",
    rationale: "The CRTC requires firms to download a fresh version of the National DNCL at least once every 31 days. This ensures that prospects who recently registered their numbers are removed from the firm's active calling lists."
  },
  {
    id: 15,
    question: "If FINTRAC discovers that an Investment Dealer intentionally failed to file a Suspicious Transaction Report (STR) regarding a massive money laundering scheme, what is the maximum penalty under the PCMLTFA?",
    options: { A: "A $100,000 fine and suspension of CIRO membership", B: "A $5,000,000 fine", C: "Up to $2,000,000 in fines and up to 5 years in prison", D: "A mandatory 30-day business closure" },
    answer: "C",
    rationale: "Money laundering is a federal crime, not just a regulatory slap on the wrist. Failing to report suspicious activity carries massive criminal liability, including multi-million dollar fines and severe jail time."
  },
  {
    id: 16,
    question: "Which system is a mandatory web-based database used by the Canadian securities industry to file registration forms, track advisor employment history, and pay regulatory fees across all jurisdictions?",
    options: { A: "SEDAR+", B: "NRD (National Registration Database)", C: "UMID (Universal Market Integrity Database)", D: "OBSI" },
    answer: "B",
    rationale: "The NRD is the digital filing cabinet for human beings and firms in the industry. Whenever you change jobs, change your address, or add an Outside Business Activity, it is updated on the NRD."
  },
  {
    id: 17,
    question: "What is the fundamental difference between the 'Right of Withdrawal' and the 'Right of Rescission' when purchasing a new securities issue?",
    options: { A: "Withdrawal gives you 2 days to cancel for any reason; Rescission gives you the right to cancel or sue for damages if you discover a material misrepresentation in the prospectus", B: "Withdrawal applies to stocks; Rescission applies to mutual funds", C: "Withdrawal is a federal right; Rescission is a provincial right", D: "They are the exact same legal concept" },
    answer: "A",
    rationale: "Withdrawal = 'Buyer's remorse' (48 hours to just change your mind). Rescission = 'You lied to me' (you find out the prospectus contained false information, giving you the legal right to unwind the trade and seek damages)."
  },
  {
    id: 18,
    question: "A bank tells a commercial client it will lower their loan interest rate by 0.5% if the client also opens a corporate investment account with the bank's wealth division. Is this a violation of the Bank Act?",
    options: { A: "Yes, this is illegal Coercive Tied Selling", B: "No, this is Preferential Pricing (Discount Bundling), which is legal as long as the loan wasn't outright refused without the investment account", C: "Yes, it violates PIPEDA", D: "Yes, it violates CIRO's Best Interest standard" },
    answer: "B",
    rationale: "Coercion ('Buy product B or I will deny you Product A') is highly illegal. Bundling ('You can have Product A either way, but I'll give you a discount if you also buy Product B') is completely legal and actively encourages market competition."
  },
  {
    id: 19,
    question: "A client wants to read the latest Annual Information Form (AIF), prospectus, and continuous disclosure documents for a publicly traded Canadian mining company. Which electronic system must they use?",
    options: { A: "SEDAR+ (System for Electronic Document Analysis and Retrieval)", B: "SEDI", C: "NRD", D: "CIPF" },
    answer: "A",
    rationale: "SEDAR+ is the public database where corporations and mutual funds are legally required to file all their official documents (prospectuses, financial statements, material change reports) so investors can read them."
  },
  {
    id: 20,
    question: "Under the PCMLTFA, what is the exact timeline an Investment Dealer has to file a Suspicious Transaction Report (STR) to FINTRAC?",
    options: { A: "Within 15 calendar days", B: "Within 24 hours", C: "As soon as practicable after measures have been taken to establish reasonable grounds for suspicion", D: "Within 30 business days" },
    answer: "C",
    rationale: "Unlike the Large Cash Transaction Report (which is a strict 15-day math rule), an STR requires human investigation. The law says you must report it 'as soon as practicable' once your compliance team confirms the activity is genuinely suspicious."
  },
  {
    id: 21,
    question: "An Approved Person suspects a client is laundering money and files an STR. During their next meeting, the client asks why their trades are taking longer to process. What is the Approved Person strictly prohibited from doing?",
    options: { A: "Charging the client an administrative fee", B: "Tipping off the client that an STR has been filed or that they are under investigation", C: "Processing any further trades for the client", D: "Looking at the client's bank statements" },
    answer: "B",
    rationale: "Tipping off a suspect destroys criminal investigations. If you tell a money launderer that FINTRAC is watching, they will immediately wire the funds offshore and destroy the evidence. 'Tipping off' is a criminal offense in itself."
  },
  {
    id: 22,
    question: "If a retail client brings a dispute to OBSI and OBSI rules entirely in the client's favor, what is the absolute maximum compensation limit OBSI can recommend?",
    options: { A: "$100,000", B: "$350,000", C: "$500,000", D: "$1,000,000" },
    answer: "B",
    rationale: "OBSI's mandate caps their recommendations at $350,000. If a client suffered a $1,000,000 loss due to bad advice, they would need the court system or binding arbitration to recover the full amount — OBSI cannot legally recommend more than $350k."
  },
  {
    id: 23,
    question: "The Federal Government announces a massive new infrastructure spending bill and a simultaneous cut to corporate income taxes to stimulate the economy. What type of policy is this?",
    options: { A: "Monetary Policy", B: "Regulatory Policy", C: "Fiscal Policy", D: "Trade Policy" },
    answer: "C",
    rationale: "Fiscal Policy = Government decisions on taxation and spending. Monetary Policy = Central Bank decisions on interest rates and the money supply. Government spending bills are always Fiscal Policy."
  },
  {
    id: 24,
    question: "Which of the following National Instruments dictates the rules for Mutual Fund operations, including strict limits on their use of leverage, short selling, and illiquid assets?",
    options: { A: "NI 31-103", B: "NI 45-106", C: "NI 81-102", D: "NI 54-101" },
    answer: "C",
    rationale: "NI 81-102 is the mutual fund rulebook. It places strict guardrails on what retail mutual funds can and cannot do to protect investors from catastrophic hedge-fund-style blowups."
  },
  {
    id: 25,
    question: "A prospect's phone number is on the National Do Not Call List (DNCL). However, the individual explicitly filled out a 'Contact Me' form on an advisor's website yesterday. Can the advisor legally call them?",
    options: { A: "No, the DNCL is absolute", B: "Yes, the online inquiry establishes an 'Express Consent' exemption, allowing the advisor to call them", C: "Yes, but only to send a text message, not a voice call", D: "No, the advisor must wait 30 days before calling" },
    answer: "B",
    rationale: "The DNCL prevents unsolicited cold calls. If someone actively asks you to call them (express consent), the DNCL does not apply to that specific interaction."
  },
  {
    id: 26,
    question: "How is the Canadian Investor Protection Fund (CIPF) primarily funded to ensure it has enough capital to pay out millions if a major dealer goes bankrupt?",
    options: { A: "Through a mandatory tax levied on all retail investor trades", B: "Through the Bank of Canada printing money", C: "Through quarterly assessments (premiums) paid by all CIRO Dealer Members based on their size and risk profile", D: "Through federal income taxes" },
    answer: "C",
    rationale: "CIPF is an industry-funded insurance policy. Retail clients do not pay for it directly. Every CIRO member firm pays premiums into the CIPF pool, ensuring that if one firm fails, the industry as a whole bails out the clients."
  },
  {
    id: 27,
    question: "PIPEDA's 'Limiting Collection' principle dictates that an Investment Dealer must do what?",
    options: { A: "Collect as much data as possible to build a strong marketing database", B: "Only collect the personal information strictly necessary for the identified purposes of the account (e.g., AML and KYC requirements)", C: "Limit the collection of data to physical paper only", D: "Limit data collection to individuals over the age of 18" },
    answer: "B",
    rationale: "You cannot ask a client for their medical history or religious affiliation if they are just opening a TFSA. You are legally required to limit your data collection only to what you absolutely need to fulfill regulatory requirements."
  },
  {
    id: 28,
    question: "CIRO was officially formed in 2023 through the amalgamation of which two previous self-regulatory organizations?",
    options: { A: "OSFI and the CSA", B: "IIROC and the MFDA", C: "The TSX and the CDCC", D: "FINTRAC and FCAC" },
    answer: "B",
    rationale: "The Investment Industry Regulatory Organization of Canada (IIROC) oversaw stock brokers and investment dealers. The Mutual Fund Dealers Association (MFDA) oversaw mutual fund reps. They merged in 2023 to create a single, unified regulator: CIRO."
  },
  {
    id: 29,
    question: "Which level of government holds the constitutional authority to regulate the securities industry and enforce capital markets laws in Canada?",
    options: { A: "The Federal Government", B: "Municipal Governments", C: "Provincial and Territorial Governments", D: "The Bank of Canada" },
    answer: "C",
    rationale: "Unlike the USA (which has the federal SEC), securities regulation in Canada falls strictly under provincial jurisdiction (e.g., the Ontario Securities Commission, the Alberta Securities Commission)."
  },
  {
    id: 30,
    question: "Under CASL, what is the crucial difference between 'Implied Consent' and 'Express Consent' when sending marketing emails?",
    options: { A: "Implied consent lasts forever; Express consent expires after 2 years", B: "Express consent requires a positive opt-in action and lasts until the user unsubscribes; Implied consent is based on a relationship or inquiry and has strict mathematical expiration dates (6 or 24 months)", C: "Express consent is for phone calls; Implied consent is for emails", D: "They are legally identical" },
    answer: "B",
    rationale: "Express consent is the holy grail for marketers because it never expires (unless revoked). Implied consent is a ticking clock — if they bought something, you have 24 months. If they just asked a question, you only have 6 months."
  },
  {
    id: 31,
    question: "A client holds an individual cash account with $800,000 and a 50% share of a joint margin account worth $500,000 at a CIRO-regulated dealer. The dealer becomes insolvent. What is the exact dollar amount of the client's assets that will NOT be covered by CIPF?",
    options: { A: "$50,000", B: "$0 (fully covered)", C: "$300,000", D: "$250,000" },
    answer: "A",
    rationale: "Client's share of joint account = 50% × $500k = $250k. Total general exposure = $800k + $250k = $1,050,000. CIPF caps general coverage at $1,000,000. Uncovered amount = $1,050,000 − $1,000,000 = $50,000."
  },
  {
    id: 32,
    question: "Under CASL, which of the following is a legally acceptable way to obtain 'Express Consent' from a prospect to send them marketing emails?",
    options: { A: "A pre-checked box on a web form that says, 'Leave checked to receive our newsletter'", B: "Verbally asking them at a noisy trade show without writing it down", C: "An empty, unchecked box on an online application that the prospect must actively click to agree to receive promotional emails", D: "Simply copying their email address from their public LinkedIn profile" },
    answer: "C",
    rationale: "Express consent requires a positive 'opt-in' action. CASL explicitly prohibits 'opt-out' mechanisms like pre-checked boxes. The prospect must take an active physical step (clicking an empty box) to legally grant express consent."
  },
  {
    id: 33,
    question: "A client deposits $9,500 CAD in physical cash. At the same time, they wire $50,000 CAD electronically from their bank to their brokerage account. What must the dealer report to FINTRAC?",
    options: { A: "Both an LCTR and an Electronic Funds Transfer (EFT) report", B: "Neither; no reporting thresholds were triggered", C: "Only an LCTR", D: "Only a Suspicious Transaction Report (STR)" },
    answer: "B",
    rationale: "The physical cash was $9,500 — under the $10,000 LCTR threshold. Electronic bank wires do not count as physical cash. Domestic electronic transfers do not trigger the international EFT reporting threshold. Therefore, no automatic reporting is required."
  },
  {
    id: 34,
    question: "Under OBSI rules, what is the 'Limitation Period' for a client to bring a complaint to OBSI after they first knew (or ought to have known) about the issue?",
    options: { A: "180 days", B: "2 years", C: "6 years", D: "10 years" },
    answer: "C",
    rationale: "Do not confuse the escalation window (180 days after firm denial) with the limitation period! The absolute maximum look-back window for OBSI to even accept a case is 6 years from when the client should have realized there was a problem."
  },
  {
    id: 35,
    question: "Which PIPEDA principle dictates that clients must be able to view the personal information an Investment Dealer holds about them and challenge the accuracy of that data?",
    options: { A: "Safeguards", B: "Identifying Purposes", C: "Limiting Collection", D: "Individual Access" },
    answer: "D",
    rationale: "The 'Individual Access' principle means an organization cannot hold a secret dossier on a client. If a client asks 'What data do you have on me?', the firm is legally required to show them the file and allow them to correct any errors."
  },
  {
    id: 36,
    question: "A corporate CFO exercises stock options to buy 5,000 shares of their own company. Within what strict timeframe must this transaction be filed on SEDI?",
    options: { A: "Within 24 hours", B: "Within 5 calendar days of the trade", C: "Within 15 business days", D: "At the end of the fiscal quarter" },
    answer: "B",
    rationale: "To ensure public transparency on what executives are doing with their own company's stock, SEDI rules mandate reporting within exactly 5 calendar days of the transaction."
  },
  {
    id: 37,
    question: "Under the CSA's Passport System, the regulator in the jurisdiction where an Investment Dealer's head office is located is known as the:",
    options: { A: "Federal Supervisor", B: "SRO Overseer", C: "Principal Regulator", D: "National Administrator" },
    answer: "C",
    rationale: "The Passport System relies on the 'Principal Regulator' concept. If your firm is headquartered in Calgary, the Alberta Securities Commission (ASC) is your Principal Regulator. They handle your paperwork, and other provinces accept the ASC's decision."
  },
  {
    id: 38,
    question: "An investor reads a prospectus for a new stock issue and realizes it contains a massive intentional misrepresentation regarding the company's debt. What statutory right allows the investor to cancel the contract or sue the issuer for damages?",
    options: { A: "The Right of Withdrawal", B: "The Right of Subrogation", C: "The Right of Redemption", D: "The Right of Rescission" },
    answer: "D",
    rationale: "Right of Withdrawal: 2 days to back out for no reason. Right of Rescission: The right to cancel the trade or sue for damages specifically because you were lied to (misrepresentation) in the offering documents."
  },
  {
    id: 39,
    question: "If the Canadian economy is overheating, inflation hits 5%, and housing prices are spiraling out of control, what is the Bank of Canada's most likely Monetary Policy response?",
    options: { A: "Lowering the overnight interest rate to encourage more borrowing", B: "Raising the overnight interest rate to make borrowing more expensive, thereby slowing consumer spending and cooling inflation", C: "Raising corporate income taxes", D: "Printing more paper money" },
    answer: "B",
    rationale: "The Bank of Canada uses interest rates like a brake pedal. When inflation runs hot, they hike rates, making mortgages and loans expensive. People stop spending, businesses stop raising prices. (Note: Option C is Fiscal Policy, not Monetary Policy.)"
  },
  {
    id: 40,
    question: "A client has $100,000 in a federally chartered bank's high-interest savings account and $100,000 invested in the bank's proprietary mutual funds. If the bank fails, how much will CDIC reimburse the client?",
    options: { A: "$0", B: "$200,000", C: "$100,000 for the savings account only", D: "$100,000 for the mutual funds only" },
    answer: "C",
    rationale: "CDIC only insures specific low-risk deposit products (savings accounts, chequing accounts, GICs under 5 years). It never insures mutual funds, stocks, or bonds, even if purchased directly inside a bank branch."
  },
  {
    id: 41,
    question: "Which National Instrument is specifically focused on standardizing the rules, leverage limits, and operational requirements for Mutual Funds across Canada?",
    options: { A: "NI 31-103", B: "NI 45-106", C: "NI 81-102", D: "NI 54-101" },
    answer: "C",
    rationale: "NI 81-102 is the definitive 'Mutual Fund Rulebook.' It sets strict limits on what retail mutual funds can and cannot do (e.g., standard mutual funds are generally banned from heavy borrowing and short selling)."
  },
  {
    id: 42,
    question: "Under FINTRAC's AML guidelines, an individual is classified as a Foreign Politically Exposed Person (Foreign PEP). How long does this classification last?",
    options: { A: "Only while they hold the political office", B: "For exactly 5 years after they leave office", C: "For exactly 1 year after they leave office", D: "Indefinitely; once a Foreign PEP, always a Foreign PEP" },
    answer: "D",
    rationale: "Domestic PEP status expires a few years after the politician leaves office. However, because foreign corruption networks can be deeply entrenched, FINTRAC dictates that a Foreign PEP (and their immediate family members) retains that high-risk status forever."
  },
  {
    id: 43,
    question: "A client files a formal complaint stating they lost $40,000 because the stock market crashed during COVID-19. They do not allege the advisor broke any rules or gave unsuitable advice. How should compliance handle this?",
    options: { A: "The firm must automatically reimburse the $40,000", B: "The firm must report the complaint to CIRO's ComSet system", C: "The firm must respond to the client but is not required to log this as a formal regulatory complaint in ComSet because it does not allege regulatory misconduct", D: "The firm must file a Suspicious Transaction Report (STR)" },
    answer: "C",
    rationale: "CIRO doesn't regulate gravity, and it doesn't regulate stock market crashes. If a client is just mad that the economy took a dive, the firm answers them, but it doesn't trigger the heavy regulatory reporting machinery because no rules were broken."
  },
  {
    id: 44,
    question: "What is the fundamental mandate of FINTRAC (Financial Transactions and Reports Analysis Centre of Canada)?",
    options: { A: "To collect taxes from wealthy Canadians", B: "To ensure banks have enough capital reserves", C: "To act as Canada's financial intelligence unit, detecting, preventing, and deterring money laundering and the financing of terrorist activities", D: "To protect investors from unsuitable mutual fund advice" },
    answer: "C",
    rationale: "FINTRAC is the spy agency for the financial system. They collect data (LCTRs, STRs, EFTs) from banks, real estate agents, and casinos to build webs connecting organized crime and terrorist networks."
  },
  {
    id: 45,
    question: "Under the CRTC's telemarketing rules, which of the following calls is legally EXEMPT from checking the National Do Not Call List (DNCL)?",
    options: { A: "A cold call to a residential home on a Sunday", B: "A cold call to another business (B2B telemarketing)", C: "A cold call to a senior citizen", D: "No telemarketing calls are ever exempt" },
    answer: "B",
    rationale: "The DNCL is designed to protect consumers at home. Calls made strictly from one business to another business (B2B) for commercial purposes are completely exempt from the DNCL restrictions."
  },
  {
    id: 46,
    question: "A Registered Representative gets married and changes their legal last name. Under CIRO rules, within exactly how many days must their firm update this change on the NRD?",
    options: { A: "Within 30 days", B: "Within 5 business days", C: "Within 10 days", D: "At the end of the calendar year" },
    answer: "C",
    rationale: "Any material change to an Approved Person's profile (name change, address change, bankruptcy, new Outside Business Activity) carries a strict regulatory deadline. It must be updated on the NRD within exactly 10 days."
  },
  {
    id: 47,
    question: "A retail client wants to review the 'Fund Facts' document for a specific mutual fund they are considering. Which centralized Canadian database permanently stores these mandatory disclosure documents?",
    options: { A: "NRD (National Registration Database)", B: "SEDI (System for Electronic Disclosure by Insiders)", C: "SEDAR+ (System for Electronic Document Analysis and Retrieval)", D: "OBSI" },
    answer: "C",
    rationale: "SEDAR+ is the public library of the Canadian securities industry. Every prospectus, annual report, and Fund Facts document issued by a public company or mutual fund is stored here for anyone to download."
  },
  {
    id: 48,
    question: "If an Investment Dealer refuses to follow an OBSI recommendation to compensate a client $100,000 for unsuitable advice, what is OBSI's primary enforcement mechanism?",
    options: { A: "They seize the funds directly from the dealer's bank account", B: "They immediately revoke the dealer's CIRO license", C: "They sentence the branch manager to jail", D: "They publicly 'name and shame' the firm by releasing a detailed report of the firm's refusal to the media and regulators" },
    answer: "D",
    rationale: "OBSI has no legal power to force a firm to write a cheque. Their power lies entirely in reputational damage. Firms will almost always pay the settlement to avoid the disastrous PR nightmare of being publicly shamed by the national ombudsman."
  },
  {
    id: 49,
    question: "The Federal Government introduces a new budget that heavily increases spending on national healthcare and raises the capital gains inclusion rate. This action is an example of:",
    options: { A: "Monetary Policy", B: "Fiscal Policy", C: "Prudential Regulation", D: "Market Integrity Regulation" },
    answer: "B",
    rationale: "Any action involving the federal budget, government taxation, or government spending is strictly defined as Fiscal Policy."
  },
  {
    id: 50,
    question: "An international client wishes to deposit $25,000 CAD equivalent via an Electronic Funds Transfer (EFT) from a bank in Switzerland. Under the PCMLTFA, what is the specific reporting requirement?",
    options: { A: "An EFT report must be filed with FINTRAC as the transaction crosses Canadian borders and exceeds the $10,000 threshold", B: "A Large Cash Transaction Report (LCTR) must be filed", C: "An STR must be filed automatically", D: "No report is needed for European transfers" },
    answer: "A",
    rationale: "Electronic Funds Transfers (EFTs) are triggered when $10,000 or more is wired internationally (either entering or leaving Canada) within a 24-hour period. Because this is a $25,000 cross-border wire, an EFT report is mandatory."
  },
  {
    id: 51,
    question: "Which of the following accounts would fall into the CIPF 'Education' category, carrying its own separate $1,000,000 coverage limit?",
    options: { A: "An RRSP", B: "A TFSA", C: "A Registered Education Savings Plan (RESP)", D: "A First Home Savings Account (FHSA)" },
    answer: "C",
    rationale: "CIPF separates accounts into distinct buckets. Cash/Margin/TFSA/FHSA fall into the 'General' bucket ($1M limit). RRSPs/RRIFs fall into the 'Retirement' bucket ($1M limit). RESPs fall entirely into their own distinct 'Education' bucket ($1M limit)."
  },
  {
    id: 52,
    question: "Unlike the United States, which relies heavily on the federal SEC, how is the securities industry constitutionally regulated in Canada?",
    options: { A: "Exclusively by the Bank of Canada", B: "Exclusively by the Federal Department of Finance", C: "By 13 individual provincial and territorial securities regulators", D: "By the RCMP" },
    answer: "C",
    rationale: "Securities regulation is a provincial jurisdiction in Canada. There is no 'federal SEC.' Instead, bodies like the Ontario Securities Commission (OSC) and Alberta Securities Commission (ASC) govern their respective territories and coordinate through the CSA."
  },
  {
    id: 53,
    question: "PIPEDA's 'Consent' principle requires that a client's consent for the collection and use of their personal data must be:",
    options: { A: "Meaningful — the client must clearly understand what they are consenting to and how the data will be used", B: "Implied for all financial transactions automatically", C: "Permanent and irrevocable", D: "Verbal only; written consent is prohibited" },
    answer: "A",
    rationale: "You cannot hide a data-sharing clause in page 40 of a legal document in size-2 font. PIPEDA requires 'meaningful consent,' meaning a reasonable person would easily understand exactly what data you are taking and who you are giving it to."
  },
  {
    id: 54,
    question: "An Approved Person determines there are 'reasonable grounds to suspect' a client is engaged in money laundering and notifies the AML Compliance Officer. When must the firm file the STR to FINTRAC?",
    options: { A: "Within exactly 15 calendar days", B: "By the end of the fiscal quarter", C: "As soon as practicable after the suspicion is confirmed", D: "Within 5 business days" },
    answer: "C",
    rationale: "STRs do not have a hard mathematical countdown clock (unlike the 15-day rule for LCTRs) because investigations take time. The law states it must be filed 'as soon as practicable' once the compliance officer officially agrees the activity is suspicious."
  },
  {
    id: 55,
    question: "A client asks if their mutual fund portfolio at an independent CIRO-regulated Dealer is protected by CDIC. What is the correct response?",
    options: { A: "Yes, up to $100,000", B: "Yes, up to $1,000,000", C: "No, CDIC only protects eligible deposits at banks. However, the assets at the CIRO dealer are protected against firm insolvency by CIPF", D: "No, mutual funds are completely unprotected by any organization" },
    answer: "C",
    rationale: "CDIC protects bank savings accounts. CIPF protects investment accounts at brokerage firms. If the CIRO dealer goes bankrupt, CIPF steps in to replace any missing mutual funds or cash in the account."
  },
  {
    id: 56,
    question: "Changes to the Bank of Canada's overnight rate take how long to fully work their way through the Canadian economy?",
    options: { A: "24 hours", B: "1 to 2 weeks", C: "12 to 18 months", D: "10 years" },
    answer: "C",
    rationale: "Monetary policy acts with a massive lag. If the Bank of Canada raises rates today, it takes roughly 12 to 18 months for those higher mortgage rates and business loan costs to actually slow down spending and bring inflation numbers down."
  },
  {
    id: 57,
    question: "Under CASL, if an individual signs up for your firm's monthly financial newsletter by actively clicking a subscription button on your website, what kind of consent is this and when does it expire?",
    options: { A: "Implied Consent; expires in 6 months", B: "Implied Consent; expires in 24 months", C: "Express Consent; expires in 3 years", D: "Express Consent; never expires unless the individual unsubscribes" },
    answer: "D",
    rationale: "Because the user took an active, positive action to opt-in (clicking the subscribe button), they have granted 'Express Consent.' This is permanent until they decide they don't want the emails anymore and hit unsubscribe."
  },
  {
    id: 58,
    question: "An Investment Dealer receives a written complaint and completes its investigation within 45 days, issuing a Substantive Response Letter denying the claim. Does the 90-Day Rule still apply?",
    options: { A: "Yes, the firm must wait the full 90 days before mailing the letter", B: "No, the firm met its obligation by providing the substantive response within the 90-day maximum deadline", C: "Yes, the firm must offer a partial settlement if done under 90 days", D: "The 90-day rule only applies to OBSI" },
    answer: "B",
    rationale: "The '90-Day Rule' is a maximum ceiling, not a minimum wait time. If the firm can finish the investigation and issue the final decision in 45 days, they have successfully complied with the timeline."
  },
  {
    id: 59,
    question: "A client purchases $50,000 of newly issued shares via a prospectus on a Monday. On Tuesday evening, they change their mind and want to back out. Can they?",
    options: { A: "No, all securities trades are final upon execution", B: "Yes, they can utilize the statutory Right of Withdrawal, which allows them to cancel without penalty within 2 business days of receiving the prospectus", C: "Yes, but they must pay a 10% cancellation fee", D: "Yes, but only if they prove the prospectus contained a lie" },
    answer: "B",
    rationale: "The Right of Withdrawal is a legally mandated 48-hour 'buyer's remorse' window specifically for new securities issues (prospectus offerings). They do not need a reason to cancel within this timeframe."
  },
  {
    id: 60,
    question: "The primary vehicle used by the Canadian Securities Administrators (CSA) to ensure securities rules are applied identically across all participating provinces is called a:",
    options: { A: "Federal Charter", B: "Provincial Decree", C: "National Instrument", D: "CIRO Bylaw" },
    answer: "C",
    rationale: "When provincial regulators agree on a new rule (e.g., the Client Focused Reforms), they draft it as a 'National Instrument' (e.g., NI 31-103). Once adopted, it ensures the rules of the game are harmonized across the entire country."
  },
  {
    id: 61,
    question: "On Wednesday morning, a client deposits $6,000 CAD in physical cash. Later that evening, they deposit $3,500 Euros in physical cash. The exchange rate is 1 Euro = 1.50 CAD. What is the firm's strict reporting requirement to FINTRAC?",
    options: { A: "File a Suspicious Transaction Report (STR)", B: "File a Large Cash Transaction Report (LCTR) within 15 calendar days because the aggregate 24-hour total exceeds $10,000 CAD", C: "No reporting is required because the currencies are different and cannot be mixed", D: "File an Electronic Funds Transfer (EFT) report" },
    answer: "B",
    rationale: "The '24-Hour Rule' applies regardless of currency type. Convert: €3,500 × 1.50 = $5,250 CAD. Add: $5,250 + $6,000 = $11,250 CAD equivalent. Since this is ≥ $10,000, an LCTR is required within 15 days."
  },
  {
    id: 62,
    question: "An investor wants to read the official, legally filed Annual Information Form (AIF) of a publicly traded Canadian pipeline company. Which database must they search?",
    options: { A: "SEDAR+ (System for Electronic Document Analysis and Retrieval)", B: "SEDI (System for Electronic Disclosure by Insiders)", C: "NRD (National Registration Database)", D: "The CDIC Public Ledger" },
    answer: "A",
    rationale: "SEDAR+ is the central public repository where all reporting issuers (public companies and mutual funds) must electronically file their mandatory disclosure documents (prospectuses, AIFs, financial statements)."
  },
  {
    id: 63,
    question: "A client receives a Substantive Response Letter from their Investment Dealer on September 1st, formally denying their complaint. What is the absolute final deadline to escalate the matter to OBSI?",
    options: { A: "November 1st", B: "December 31st", C: "180 calendar days from September 1st (roughly end of February)", D: "Exactly 6 years from September 1st" },
    answer: "C",
    rationale: "Once the dealer issues its final decision, the client has exactly 180 days to take the dispute to OBSI."
  },
  {
    id: 64,
    question: "An Investment Dealer receives a $15,000 CAD wire transfer on behalf of a client. Under what specific condition does this trigger an Electronic Funds Transfer (EFT) report to FINTRAC?",
    options: { A: "If the wire was sent from an RRSP account", B: "If the wire originated from another Canadian province", C: "If the wire was sent to purchase a high-risk penny stock", D: "If the wire transfer crossed the Canadian border (i.e., was initiated in a foreign country and received in Canada)" },
    answer: "D",
    rationale: "Domestic wires do not trigger an EFT report. FINTRAC only requires an EFT report when $10,000 or more is wired internationally (entering or leaving Canada) within 24 hours."
  },
  {
    id: 65,
    question: "A client holds: an individual RRSP with $700,000, a LIRA (Locked-in Retirement Account) with $400,000, and a TFSA with $500,000 at a bankrupt CIRO-regulated dealer. How much will CIPF cover?",
    options: { A: "All $1,600,000 is fully covered", B: "TFSA fully covered ($500k). RRSP + LIRA combined in 'Retirement' category ($1.1M) capped at $1M. Total coverage = $1,500,000", C: "TFSA is grouped with the RRSP, capping everything at $1M total", D: "Only $1,000,000 total across all accounts" },
    answer: "B",
    rationale: "General Category: TFSA = $500,000 (fully covered under the $1M limit). Retirement Category: RRSP + LIRA = $1,100,000 (capped at $1M; $100k is lost). Total CIPF payout: $500k + $1M = $1,500,000."
  },
  {
    id: 66,
    question: "While CIRO oversees the conduct of investment dealers, which entity is responsible for directly approving and issuing a National Instrument (like NI 31-103) that governs the Canadian market?",
    options: { A: "The Canadian Securities Administrators (CSA) through the coordinated agreement of the provincial regulators", B: "The Bank of Canada", C: "The Federal Department of Finance", D: "The Ombudsman for Banking Services and Investments (OBSI)" },
    answer: "A",
    rationale: "CIRO enforces the rules, but the provincial regulators (acting collectively as the CSA) are the ones who actually write and pass the overarching securities laws (National Instruments) in Canada."
  },
  {
    id: 67,
    question: "Under PIPEDA, what is the firm strictly prohibited from doing regarding client consent for data collection?",
    options: { A: "Asking for the consent in writing", B: "Using electronic signatures to obtain the consent", C: "Making consent to the collection of unnecessary information a mandatory condition for providing the service or opening the account", D: "Allowing the client to withdraw their consent later" },
    answer: "C",
    rationale: "You cannot hold a client hostage for their data. If you only need their SIN and income to open an account, you cannot legally say 'You must also consent to us giving your phone number to marketing affiliates, or we won't open your account.'"
  },
  {
    id: 68,
    question: "A retail client purchased a proprietary mutual fund from your firm exactly 20 months ago. They have not interacted with you since. Under CASL, can you legally send them an email promoting a new ETF?",
    options: { A: "No, implied consent expires after 6 months", B: "No, marketing emails always require express written consent", C: "Yes, but only if you BCC them", D: "Yes, an existing business relationship (a purchase) grants implied consent to send marketing emails for up to 24 months" },
    answer: "D",
    rationale: "Because money changed hands (a purchase occurred), CASL grants a 2-year (24-month) window of 'implied consent.' Since it has only been 20 months, you are legally permitted to send the email (provided it contains an unsubscribe link)."
  },
  {
    id: 69,
    question: "The Canadian economy enters a severe recession with rising unemployment. Inflation drops to 0.5%. What action is the Bank of Canada most likely to take?",
    options: { A: "Increase government spending", B: "Lower the overnight interest rate to make borrowing cheaper, stimulating business investment and consumer spending", C: "Raise the overnight interest rate to protect the strength of the Canadian dollar", D: "Implement a nationwide wage freeze" },
    answer: "B",
    rationale: "When the economy is freezing cold, the Bank of Canada steps on the gas pedal. By lowering interest rates, mortgages become cheaper and businesses can take out loans to build factories and hire workers, pushing inflation back up toward the 2% target."
  },
  {
    id: 70,
    question: "A client has $120,000 in a 3-year GIC and $80,000 in a checking account at the same federally regulated bank. The bank goes bankrupt. What is the CDIC payout?",
    options: { A: "$200,000", B: "$120,000", C: "$100,000", D: "$0" },
    answer: "C",
    rationale: "CDIC provides a strict maximum of $100,000 per category, per institution. GICs (under 5 years) and checking accounts are grouped in the 'deposits held in one name' category. The client's $200k total exceeds the cap, so they only receive $100,000."
  },
  {
    id: 71,
    question: "A Registered Representative moves to a new apartment but does not tell their compliance department, leaving the NRD unupdated. This violates the rule requiring NRD updates within what strict timeframe?",
    options: { A: "Within 10 days of the change", B: "Within 30 days of the change", C: "Within 5 business days of the change", D: "Before the end of the calendar year" },
    answer: "A",
    rationale: "The NRD must remain highly accurate. Any material change to an advisor's profile (including a home address change) must be submitted to the NRD within exactly 10 days, or the firm can face late-filing fees and regulatory scrutiny."
  },
  {
    id: 72,
    question: "A client bought $50,000 of a new stock issue via a prospectus. Three months later, it is revealed the prospectus contained entirely fabricated financial statements. What statutory right allows the client to sue the issuer for their losses?",
    options: { A: "The Right of Withdrawal", B: "The Right of Redemption", C: "The Right of Subrogation", D: "The Right of Action for Damages (or Rescission)" },
    answer: "D",
    rationale: "The Right of Withdrawal expired 2 days after they got the prospectus. However, because the prospectus contained a material lie (misrepresentation), the investor has a statutory 'Right of Action for Damages' allowing them to sue to get their money back."
  },
  {
    id: 73,
    question: "While CIRO regulates the 'market conduct' of investment dealers, what is the primary focus of OSFI?",
    options: { A: "Regulating the advertising standards of mutual funds", B: "Ensuring the prudential safety and financial soundness of federally regulated financial institutions (banks, insurance companies, trust companies)", C: "Setting the overnight interest rate", D: "Managing the National Do Not Call List" },
    answer: "B",
    rationale: "OSFI is the 'bank safety' regulator. They don't handle retail complaints; they look at massive spreadsheets to ensure Canada's largest financial institutions aren't taking on too much debt and have enough cash to survive a crisis."
  },
  {
    id: 74,
    question: "An Investment Dealer uses an automated predictive dialer to cold-call prospects. To comply with CRTC telemarketing rules, how frequently must the dealer scrub their phone number list against the National DNCL?",
    options: { A: "Every 31 days", B: "Every 14 days", C: "Every 90 days", D: "Every 6 months" },
    answer: "A",
    rationale: "The CRTC requires a fresh scrub of the National Do Not Call List at least once every 31 days to ensure the firm isn't harassing Canadians who recently registered their numbers."
  },
  {
    id: 75,
    question: "A client walks into a brokerage branch with a certified bank draft for $15,000 CAD to fund their margin account. Does this trigger a Large Cash Transaction Report (LCTR) to FINTRAC?",
    options: { A: "Yes, because the amount exceeds $10,000", B: "Yes, because a bank draft is considered a suspicious instrument", C: "No, because bank drafts, personal cheques, and electronic transfers do not meet the legal definition of 'physical cash' (coins and paper money) under the LCTR rules", D: "Yes, but only if it involves US currency" },
    answer: "C",
    rationale: "This is a classic trick question. The LCTR is strictly for physical paper bills and coins. A bank draft leaves a paper trail through the banking system, so it is not considered physical cash and does not trigger an automatic mathematical LCTR."
  },
  {
    id: 76,
    question: "Under the CSA's Passport System, if an advisor registered in Ontario wants to be registered in Alberta, what is the regulatory process?",
    options: { A: "The advisor must pass the Alberta Securities Course", B: "The advisor must physically travel to Alberta for an interview", C: "The advisor's firm must pay a $10,000 fee to the Federal Government", D: "The advisor's firm submits the application through the NRD to the principal regulator (Ontario), which acts as a clearinghouse to seamlessly grant the Alberta registration" },
    answer: "D",
    rationale: "The Passport System cuts red tape. You deal with your home province (Principal Regulator). If they approve you, the other 'host' provinces automatically accept that approval."
  },
  {
    id: 77,
    question: "The Bank of Canada adjusts monetary policy to hit its target inflation rate. What specific mathematical target forms the midpoint of their inflation-control range?",
    options: { A: "2.0%", B: "1.5%", C: "3.0%", D: "0.0%" },
    answer: "A",
    rationale: "The control range is 1% to 3%, but the Bank's specific, explicit mathematical target is to keep inflation as close to 2% as possible over the medium term."
  },
  {
    id: 78,
    question: "A retail client lost $400,000 due to severe churning by their advisor. They take their case to OBSI. OBSI finds the dealer 100% at fault. What is the mathematical reality of OBSI's ruling?",
    options: { A: "OBSI can legally force the dealer to pay the full $400,000", B: "OBSI can only issue a non-binding recommendation for compensation up to $350,000 maximum; the client forfeits the remaining $50,000 if they accept the settlement", C: "OBSI cannot take the case because it exceeds their $100,000 limit", D: "OBSI will refer the case to the RCMP for a criminal refund" },
    answer: "B",
    rationale: "OBSI's power is capped at $350,000. They cannot recommend a payout higher than $350k. If the client wants the full $400,000, they must abandon the free OBSI process and hire a lawyer to sue the dealer in court or use binding arbitration."
  },
  {
    id: 79,
    question: "A client has an individual RESP holding $1,500,000. The CIRO dealer holding the account goes bankrupt. What is the mathematical CIPF payout?",
    options: { A: "$1,500,000", B: "$0, because RESPs are not covered by CIPF", C: "$1,000,000, because RESPs fall into their own distinct 'Education' category, which is capped at $1M", D: "$500,000" },
    answer: "C",
    rationale: "CIPF limits apply per category. The 'Education' category (RESPs) is entirely separate from the General or Retirement categories, and it carries its own distinct maximum cap of $1,000,000. The client loses the remaining $500k."
  },
  {
    id: 80,
    question: "Under CASL, which of the following scenarios is completely EXEMPT from CASL's consent and formatting requirements?",
    options: { A: "An email sent to a former client", B: "An email sent to a prospect who requested a quote", C: "An email sent to an individual who checked an opt-in box", D: "A strictly Business-to-Business (B2B) email sent to an employee of another organization, provided the message is relevant to their business role or duties" },
    answer: "D",
    rationale: "CASL is primarily designed to stop consumer spam. If you are emailing the CFO of a corporation pitching a corporate group RRSP plan, this is a B2B communication. As long as the email is relevant to their job, it is exempt from strict CASL consent rules."
  },
  {
    id: 81,
    question: "While CIRO enforces regulatory rules and issues fines to advisors, who holds the constitutional power to criminally prosecute an Approved Person for massive money laundering under the PCMLTFA?",
    options: { A: "The Federal Government (RCMP and the Department of Justice)", B: "The Canadian Securities Administrators (CSA)", C: "FINTRAC", D: "The Ombudsman for Banking Services and Investments (OBSI)" },
    answer: "A",
    rationale: "FINTRAC collects the data. CIRO hands out regulatory fines. But money laundering is a severe crime under the Criminal Code of Canada. Only federal law enforcement (RCMP) and the Crown can throw an advisor in federal prison."
  },
  {
    id: 82,
    question: "Under AML regulations, what does the acronym PEP stand for?",
    options: { A: "Primary Equity Participant", B: "Private Enterprise Partner", C: "Politically Exposed Person", D: "Personal Estate Planner" },
    answer: "C",
    rationale: "A Politically Exposed Person is someone entrusted with a prominent public function (like a mayor, judge, or military general). Because of their power, they have a higher probability of being involved in bribery or corruption, requiring enhanced AML scrutiny."
  },
  {
    id: 83,
    question: "The CEO of a TSX-listed company decides to sell 50% of their personal stock in the company to buy a yacht. By what deadline must the CEO report this trade via SEDI?",
    options: { A: "Within 30 days", B: "Within 5 calendar days of the trade execution", C: "Within 24 hours", D: "Before the trade is even placed" },
    answer: "B",
    rationale: "Legal insider trading (executives buying/selling their own stock) is perfectly legal, provided it is transparent. The strict rule is that it must be filed on SEDI within 5 calendar days."
  },
  {
    id: 84,
    question: "PIPEDA's 'Openness' principle requires an Investment Dealer to do what?",
    options: { A: "Publish all employee salaries online", B: "Share client data with all other Canadian banks", C: "Leave their physical branch doors open 24/7", D: "Make their detailed policies and practices regarding the management of personal information easily available and readily understandable to the public" },
    answer: "D",
    rationale: "'Openness' means transparency. A client shouldn't have to hire a lawyer to figure out how a firm handles their data. The firm must have a clear, plain-language Privacy Policy posted publicly (usually on their website)."
  },
  {
    id: 85,
    question: "A retail client buys $10,000 of a Mutual Fund. After reading the Fund Facts document that arrived in the mail the next day, they realize the fund's risk profile is too high. What is their statutory right?",
    options: { A: "The Right of Withdrawal, allowing them to cancel the trade without penalty within two business days of receiving the disclosure document", B: "The Right of Rescission, allowing them to sue the fund manager", C: "They have no rights; mutual fund sales are instantly final", D: "They can only sell the fund at the current market price, absorbing any losses" },
    answer: "A",
    rationale: "Just like new stock issues, mutual funds are sold by prospectus (or a Fund Facts equivalent). The Right of Withdrawal gives retail investors an absolute 48-hour cooling-off period to reverse the trade."
  },
  {
    id: 86,
    question: "A branch manager officially concludes there are reasonable grounds to suspect a client is using their account to launder illicit funds. What is the timeline for submitting the STR to FINTRAC?",
    options: { A: "Within 15 calendar days", B: "Within 24 hours", C: "As soon as practicable", D: "Within 30 business days" },
    answer: "C",
    rationale: "Unlike LCTRs which have a rigid 15-day math rule, STRs must be filed 'as soon as practicable' after the firm has officially determined the activity is suspicious, acknowledging that investigations take time to assemble."
  },
  {
    id: 87,
    question: "What is the primary purpose of the National Registration Database (NRD)?",
    options: { A: "To track the daily trading volume of the TSX", B: "To provide a single secure electronic system for dealers and advisors to submit registration applications, track employment history, and pay fees to provincial regulators", C: "To publish corporate prospectuses to the public", D: "To report suspicious transactions to FINTRAC" },
    answer: "B",
    rationale: "The NRD is the HR and licensing filing cabinet for the entire Canadian securities industry, allowing regulators to track who is licensed to sell what, across all provinces."
  },
  {
    id: 88,
    question: "A client's portfolio drops from $500,000 to $200,000 during a severe market recession. The client's CIRO-regulated dealer remains highly profitable and operational. The client files a claim with CIPF for the $300,000 loss. What will CIPF pay?",
    options: { A: "$300,000", B: "$150,000", C: "$350,000", D: "$0" },
    answer: "D",
    rationale: "CIPF is NOT market loss insurance. It only triggers if the dealer actually goes bankrupt and the client's assets are physically missing. Because the firm is still operating, CIPF pays nothing."
  },
  {
    id: 89,
    question: "A bank loan officer tells a client: 'I will approve your $500,000 mortgage, but only on the mandatory condition that you transfer your $200,000 RRSP to our bank's mutual fund division.' What illegal practice is this?",
    options: { A: "Coercive Tied Selling, which is strictly prohibited under the Bank Act", B: "Preferential Pricing, which is legal", C: "Wash Trading", D: "Front Running" },
    answer: "A",
    rationale: "You cannot hold a credit product (a loan/mortgage) hostage to force a client to buy an investment product. This is coercion and is highly illegal under the Bank Act."
  },
  {
    id: 90,
    question: "A Canadian Mayor is classified as a Domestic PEP, while a European Senator is classified as a Foreign PEP. What is the crucial regulatory difference regarding how long these classifications last?",
    options: { A: "Domestic PEP status lasts forever; Foreign PEP status expires after 5 years", B: "Domestic PEP status generally expires a few years after the individual leaves office; Foreign PEP status lasts indefinitely (forever)", C: "There is no difference; both expire immediately upon leaving office", D: "Domestic PEPs require STRs; Foreign PEPs do not" },
    answer: "B",
    rationale: "Because foreign corruption webs can be incredibly deep and difficult for Canadian authorities to verify, FINTRAC rules state that if someone is a Foreign PEP, they (and their immediate family members) hold that high-risk status for life."
  },
  {
    id: 91,
    question: "Under CASL, an individual sent an email to your firm asking a general question about your fees exactly 7 months ago. They did not buy anything. Can you legally send them a commercial marketing email today?",
    options: { A: "Yes, inquiries grant 12 months of implied consent", B: "Yes, inquiries grant 24 months of implied consent", C: "No, an inquiry only grants implied consent for exactly 6 months. Since 7 months have passed, the consent has expired and sending the email is illegal", D: "Yes, financial services are exempt from CASL" },
    answer: "C",
    rationale: "This is a strict mathematical timeline: Inquiry = 6 months. Purchase = 24 months. Because they only asked a question and 7 months have passed, you have zero legal right to email them."
  },
  {
    id: 92,
    question: "To ensure the securities industry operates smoothly across provincial borders, the CSA drafts harmonized rules. What are these rules officially called?",
    options: { A: "Federal Decrees", B: "Provincial Statutes", C: "CIRO Bylaws", D: "National Instruments" },
    answer: "D",
    rationale: "When the provincial regulators agree on a rule (like NI 31-103 for registration, or NI 81-102 for mutual funds), it is published as a 'National Instrument,' ensuring a unified rulebook across the country."
  },
  {
    id: 93,
    question: "If a client wins a dispute at OBSI, does the Investment Dealer legally have to pay the recommended compensation?",
    options: { A: "No, OBSI recommendations are not legally binding. However, if a firm refuses, OBSI will publicly 'name and shame' them, causing severe reputational damage", B: "Yes, OBSI is a court of law and can seize assets", C: "Yes, OBSI can instantly revoke the firm's CIRO license", D: "No, the firm can just ignore it with no consequences" },
    answer: "A",
    rationale: "OBSI is an ombudsman, not a judge. They cannot force a firm to pay. However, the threat of being publicly blasted in the media for ripping off a retail client is usually enough to make the firm write the cheque."
  },
  {
    id: 94,
    question: "The Federal Government announces an increase in the capital gains inclusion rate to generate more tax revenue to pay down the national debt. This action is a prime example of:",
    options: { A: "Monetary Policy", B: "Fiscal Policy", C: "Market Integrity Policy", D: "Prudential Policy" },
    answer: "B",
    rationale: "Taxation and government debt are the exclusive domains of Fiscal Policy (controlled by the elected government). Monetary Policy (interest rates) is controlled by the Bank of Canada."
  },
  {
    id: 95,
    question: "A client opens an account but notes that their spouse will be funding the account and making the actual trading decisions. Under AML rules, what mandatory document must the firm complete?",
    options: { A: "A Joint Account Agreement", B: "A Suspicious Transaction Report", C: "A Third-Party Determination, to document the spouse's identity and relationship to the account to ensure it is not being used to hide illicit activity", D: "A Margin Agreement" },
    answer: "C",
    rationale: "Whenever the person listed on the account is not the person pulling the strings or providing the cash, the firm must heavily document the 'Third Party.' This stops criminals from using innocent people as fronts for money laundering."
  },
  {
    id: 96,
    question: "Under the PCMLTFA (AML regulations), what is the strict minimum number of years an Investment Dealer must retain client identification records, large cash records, and STRs?",
    options: { A: "5 years", B: "7 years", C: "10 years", D: "2 years" },
    answer: "A",
    rationale: "Be careful! While the general CIRO rule for trade tickets and account forms is 7 years, the specific federal AML law (PCMLTFA) requires firms to keep FINTRAC reporting records and client ID records for a minimum of 5 years."
  },
  {
    id: 97,
    question: "Which two massive self-regulatory organizations formally merged in 2023 to create the new Canadian Investment Regulatory Organization (CIRO)?",
    options: { A: "The CSA and OSFI", B: "The TSX and the CDCC", C: "FINTRAC and OBSI", D: "The Investment Industry Regulatory Organization of Canada (IIROC) and the Mutual Fund Dealers Association of Canada (MFDA)" },
    answer: "D",
    rationale: "Historically, stockbrokers were regulated by IIROC, and mutual fund reps were regulated by the MFDA. They merged to create CIRO, a single, streamlined regulator for all investment dealers in Canada."
  },
  {
    id: 98,
    question: "A client realizes in 2024 that their advisor executed highly unsuitable trades back in 2015, causing massive losses. They file a complaint with OBSI. What will OBSI mathematically do?",
    options: { A: "Investigate and recommend up to $350,000 in compensation", B: "Reject the case entirely, because the event occurred outside of OBSI's strict 6-year Limitation Period", C: "Refer the case to the RCMP", D: "Force the dealer to pay the full loss" },
    answer: "B",
    rationale: "OBSI will not investigate ancient history. The maximum look-back window (limitation period) is 6 years from the time the client knew, or should have known, about the problem. 2015 to 2024 is 9 years — the case is instantly dismissed."
  },
  {
    id: 99,
    question: "An entrepreneur holds $1,000,000 in a personal non-registered cash account, and $1,000,000 in a corporate account for their 100%-owned holding company. Both accounts are at a CIRO dealer that goes bankrupt. What is the mathematical CIPF payout?",
    options: { A: "$1,000,000 total, because the individual owns the corporation, grouping them together", B: "$0, because corporate accounts are not covered by CIPF", C: "$2,000,000 total, because a corporation is a distinct separate legal entity, meaning the corporate account gets its own $1,000,000 limit, separate from the individual's personal limit", D: "$1,500,000 total" },
    answer: "C",
    rationale: "A corporation is legally a 'separate person.' Therefore, the holding company gets its own $1 Million General category limit, completely independent of the actual human owner's personal $1 Million limit. Both accounts are fully protected = $2,000,000."
  },
  {
    id: 100,
    question: "Ultimately, what is the overriding foundational goal of the entire Canadian regulatory framework (CIRO, CSA, OSFI, and FINTRAC) outlined in Element 1?",
    options: { A: "To protect investors, ensure fair, efficient, and transparent markets, and reduce systemic risk and financial crime in the Canadian economy", B: "To guarantee that all retail investors make a minimum 5% return", C: "To maximize the profitability of Canada's major banks", D: "To eliminate all taxes for foreign investors" },
    answer: "A",
    rationale: "This is the core thesis of your entire career. The rules exist to ensure the public trusts the financial system. If people don't trust the markets, they won't invest. If they don't invest, the economy collapses. Every rule you just mastered is designed to maintain that critical trust."
  }
];

const TOPICS = [
  { label: "All Questions", value: "all" },
  { label: "CIPF Coverage", value: "cipf" },
  { label: "FINTRAC / AML", value: "fintrac" },
  { label: "CASL", value: "casl" },
  { label: "Regulators & Bodies", value: "regulators" },
  { label: "OBSI", value: "obsi" },
  { label: "CDIC", value: "cdic" },
  { label: "Monetary & Fiscal Policy", value: "policy" },
  { label: "Registration & NRD", value: "registration" },
];

const topicMap = {
  cipf: [1, 26, 31, 51, 55, 65, 79, 88, 99],
  fintrac: [5, 15, 20, 21, 33, 42, 44, 50, 54, 61, 64, 75, 81, 82, 86, 90, 95, 96],
  casl: [3, 25, 30, 32, 57, 67, 68, 80, 91],
  regulators: [2, 4, 10, 24, 28, 29, 52, 60, 66, 92, 97, 100],
  obsi: [8, 13, 22, 34, 48, 63, 78, 93, 98],
  cdic: [10, 40, 55, 70],
  policy: [9, 23, 39, 49, 56, 69, 77, 94],
  registration: [7, 16, 19, 37, 46, 47, 62, 76, 87],
};

export default function CIROElement1() {
  const [selectedTopic, setSelectedTopic] = useState("all");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showRationale, setShowRationale] = useState({});
  const [quizMode, setQuizMode] = useState("practice"); // "practice" | "review" | "results"
  const [showTopicMenu, setShowTopicMenu] = useState(false);

  const filteredQuestions = useMemo(() => {
    if (selectedTopic === "all") return questions;
    const ids = topicMap[selectedTopic] || [];
    return questions.filter(q => ids.includes(q.id));
  }, [selectedTopic]);

  const current = filteredQuestions[currentIdx];
  const totalQ = filteredQuestions.length;

  const answeredCount = Object.keys(selectedAnswers).filter(k =>
    filteredQuestions.some(q => q.id === Number(k))
  ).length;

  const correctCount = Object.entries(selectedAnswers).filter(([id, ans]) => {
    const q = questions.find(q => q.id === Number(id));
    return q && q.answer === ans;
  }).length;

  const handleAnswer = (optKey) => {
    if (selectedAnswers[current.id]) return;
    setSelectedAnswers(prev => ({ ...prev, [current.id]: optKey }));
    setShowRationale(prev => ({ ...prev, [current.id]: true }));
  };

  const progress = totalQ > 0 ? ((currentIdx + 1) / totalQ) * 100 : 0;
  const answeredPct = totalQ > 0 ? (answeredCount / totalQ) * 100 : 0;

  const selected = selectedAnswers[current?.id];
  const revealed = showRationale[current?.id];

  const isCorrect = selected && current && selected === current.answer;

  const optColors = {
    default: { bg: "#f8fafc", border: "#e2e8f0", text: "#334155", hover: "#f1f5f9" },
    correct: { bg: "#dcfce7", border: "#16a34a", text: "#166534" },
    incorrect: { bg: "#fee2e2", border: "#dc2626", text: "#991b1b" },
    revealed: { bg: "#dbeafe", border: "#2563eb", text: "#1e40af" },
  };

  const getOptStyle = (optKey) => {
    if (!selected) return "option-default";
    if (optKey === current.answer) return "option-correct";
    if (optKey === selected && selected !== current.answer) return "option-incorrect";
    return "option-dimmed";
  };

  const resetQuiz = () => {
    setSelectedAnswers({});
    setShowRationale({});
    setCurrentIdx(0);
    setQuizMode("practice");
  };

  const scoreColor = correctCount / answeredCount >= 0.8 ? "#166534" : correctCount / answeredCount >= 0.6 ? "#92400e" : "#991b1b";
  const scoreBg = correctCount / answeredCount >= 0.8 ? "#dcfce7" : correctCount / answeredCount >= 0.6 ? "#fef3c7" : "#fee2e2";

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'system-ui', -apple-system, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .serif { font-family: 'Playfair Display', Georgia, serif; }
        .btn-primary {
          background: #1e40af; color: #fff; border: none; border-radius: 8px;
          padding: 10px 22px; font-size: 14px; font-weight: 600; cursor: pointer;
          transition: background 0.15s;
        }
        .btn-primary:hover { background: #1d3fa0; }
        .btn-secondary {
          background: #fff; color: #334155; border: 1.5px solid #e2e8f0;
          border-radius: 8px; padding: 9px 20px; font-size: 14px;
          font-weight: 500; cursor: pointer; transition: all 0.15s;
        }
        .btn-secondary:hover { border-color: #94a3b8; background: #f8fafc; }
        .btn-ghost {
          background: transparent; color: #64748b; border: none; border-radius: 8px;
          padding: 8px 16px; font-size: 14px; cursor: pointer; transition: all 0.15s;
        }
        .btn-ghost:hover { background: #f1f5f9; color: #334155; }
        .option-btn {
          width: 100%; text-align: left; border-radius: 10px; padding: 14px 18px;
          font-size: 14px; cursor: pointer; transition: all 0.18s;
          border: 1.5px solid #e2e8f0; background: #fff; color: #334155;
          font-family: inherit; line-height: 1.5; display: flex; gap: 12px; align-items: flex-start;
        }
        .option-btn:hover:not([disabled]) { border-color: #3b82f6; background: #eff6ff; }
        .option-btn[disabled] { cursor: default; }
        .option-default {}
        .option-correct { border-color: #16a34a !important; background: #dcfce7 !important; color: #166534 !important; }
        .option-incorrect { border-color: #dc2626 !important; background: #fee2e2 !important; color: #991b1b !important; }
        .option-dimmed { opacity: 0.45; }
        .tab-btn {
          padding: 9px 18px; border: none; background: transparent; cursor: pointer;
          font-size: 13px; font-weight: 600; color: #64748b; border-radius: 8px;
          transition: all 0.15s; letter-spacing: 0.03em;
        }
        .tab-btn.active { background: #1e40af; color: #fff; }
        .tab-btn:hover:not(.active) { background: #e2e8f0; color: #334155; }
        .topic-menu {
          position: absolute; top: calc(100% + 8px); left: 0; background: #fff;
          border: 1.5px solid #e2e8f0; border-radius: 10px; z-index: 100;
          box-shadow: 0 8px 24px rgba(0,0,0,0.09); min-width: 220px; overflow: hidden;
        }
        .topic-item {
          display: block; width: 100%; text-align: left; padding: 10px 16px;
          background: transparent; border: none; cursor: pointer; font-size: 13px;
          color: #334155; transition: background 0.12s;
        }
        .topic-item:hover { background: #f1f5f9; }
        .topic-item.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
        .card { background: #fff; border-radius: 14px; border: 1.5px solid #e2e8f0; padding: 28px 30px; }
        .progress-bar { height: 3px; background: #e2e8f0; border-radius: 99px; overflow: hidden; }
        .progress-fill { height: 100%; border-radius: 99px; transition: width 0.4s ease; background: linear-gradient(90deg, #1e40af, #3b82f6); }
        .rationale-box {
          background: #eff6ff; border-left: 4px solid #3b82f6; border-radius: 8px;
          padding: 14px 16px; margin-top: 16px; font-size: 13.5px; color: #1e3a5f; line-height: 1.65;
        }
        .badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 10px; border-radius: 99px; font-size: 12px; font-weight: 600;
        }
        .q-nav-btn {
          width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #e2e8f0;
          background: #fff; display: flex; align-items: center; justify-content: center;
          cursor: pointer; font-size: 16px; color: #64748b; transition: all 0.15s;
        }
        .q-nav-btn:hover:not([disabled]) { border-color: #3b82f6; color: #1e40af; background: #eff6ff; }
        .q-nav-btn[disabled] { opacity: 0.35; cursor: default; }
        .result-item {
          display: flex; align-items: flex-start; gap: 12px; padding: 12px 16px;
          border-radius: 10px; border: 1.5px solid #e2e8f0; margin-bottom: 8px;
          cursor: pointer; transition: border-color 0.15s;
        }
        .result-item:hover { border-color: #94a3b8; }
        .dot-correct { width: 10px; height: 10px; border-radius: 50%; background: #16a34a; flex-shrink: 0; margin-top: 3px; }
        .dot-incorrect { width: 10px; height: 10px; border-radius: 50%; background: #dc2626; flex-shrink: 0; margin-top: 3px; }
        .dot-skipped { width: 10px; height: 10px; border-radius: 50%; background: #94a3b8; flex-shrink: 0; margin-top: 3px; }
      `}</style>

      {/* Sticky Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 50,
        background: "rgba(248,250,252,0.95)", backdropFilter: "blur(10px)",
        borderBottom: "1.5px solid #e2e8f0"
      }}>
        <div style={{ maxWidth: 840, margin: "0 auto", padding: "0 20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 0 10px" }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", color: "#1e40af", textTransform: "uppercase", marginBottom: 2 }}>
                CIRO Trader Exam · CIRE
              </div>
              <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
                Element 1 — Regulatory Framework
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {answeredCount > 0 && (
                <div style={{ background: scoreBg, color: scoreColor, borderRadius: 8, padding: "5px 12px", fontSize: 13, fontWeight: 700 }}>
                  {correctCount}/{answeredCount} correct
                </div>
              )}
              <div style={{ background: "#f1f5f9", color: "#475569", borderRadius: 8, padding: "5px 12px", fontSize: 13, fontWeight: 600 }}>
                {currentIdx + 1} / {totalQ}
              </div>
            </div>
          </div>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, paddingBottom: 10 }}>
            {[["practice","📝 Practice"],["review","📖 Review All"],["results","📊 Results"]].map(([mode, label]) => (
              <button key={mode} className={`tab-btn ${quizMode === mode ? "active" : ""}`}
                onClick={() => { setQuizMode(mode); if (mode !== "results") setCurrentIdx(0); }}>
                {label}
              </button>
            ))}
          </div>
          {/* Progress */}
          <div className="progress-bar" style={{ marginBottom: 14 }}>
            <div className="progress-fill" style={{ width: `${quizMode === "results" ? answeredPct : progress}%` }} />
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 840, margin: "0 auto", padding: "28px 20px 60px" }}>

        {/* Topic Filter */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b", letterSpacing: "0.06em", textTransform: "uppercase" }}>Filter by topic:</span>
          <div style={{ position: "relative" }}>
            <button className="btn-secondary" style={{ fontSize: 13, padding: "7px 14px", display: "flex", alignItems: "center", gap: 6 }}
              onClick={() => setShowTopicMenu(!showTopicMenu)}>
              {TOPICS.find(t => t.value === selectedTopic)?.label}
              <span style={{ fontSize: 10 }}>▼</span>
            </button>
            {showTopicMenu && (
              <div className="topic-menu">
                {TOPICS.map(t => (
                  <button key={t.value} className={`topic-item ${selectedTopic === t.value ? "active" : ""}`}
                    onClick={() => { setSelectedTopic(t.value); setCurrentIdx(0); setShowTopicMenu(false); }}>
                    {t.label}
                    {t.value !== "all" && <span style={{ marginLeft: 6, color: "#94a3b8" }}>({(topicMap[t.value]||[]).length})</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          {answeredCount > 0 && (
            <button className="btn-ghost" style={{ fontSize: 13, color: "#ef4444" }} onClick={resetQuiz}>
              ↺ Reset Progress
            </button>
          )}
        </div>

        {/* ── PRACTICE MODE ── */}
        {quizMode === "practice" && current && (
          <div>
            {/* Question Card */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
                <div className="badge" style={{ background: "#dbeafe", color: "#1e40af" }}>
                  Question {current.id}
                </div>
                {selected && (
                  <div className="badge" style={{
                    background: isCorrect ? "#dcfce7" : "#fee2e2",
                    color: isCorrect ? "#166534" : "#991b1b"
                  }}>
                    {isCorrect ? "✓ Correct" : "✗ Incorrect"}
                  </div>
                )}
              </div>

              <p className="serif" style={{ fontSize: 17, color: "#0f172a", lineHeight: 1.65, marginBottom: 24, fontWeight: 500 }}>
                {current.question}
              </p>

              {/* Options */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(["A","B","C","D"]).map(optKey => (
                  <button key={optKey} className={`option-btn ${getOptStyle(optKey)}`}
                    disabled={!!selected}
                    onClick={() => handleAnswer(optKey)}>
                    <span style={{ fontWeight: 700, flexShrink: 0, fontSize: 13 }}>{optKey}.</span>
                    <span>{current.options[optKey]}</span>
                    {selected && optKey === current.answer && (
                      <span style={{ marginLeft: "auto", flexShrink: 0, fontSize: 16 }}>✓</span>
                    )}
                    {selected && optKey === selected && selected !== current.answer && (
                      <span style={{ marginLeft: "auto", flexShrink: 0, fontSize: 16 }}>✗</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Rationale */}
              {revealed && (
                <div className="rationale-box">
                  <div style={{ fontWeight: 700, marginBottom: 5, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1e40af" }}>
                    📘 Rationale
                  </div>
                  {current.rationale}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <button className="q-nav-btn" disabled={currentIdx === 0}
                onClick={() => setCurrentIdx(i => i - 1)}>‹</button>

              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "center", maxWidth: 600 }}>
                {filteredQuestions.slice(Math.max(0, currentIdx - 4), Math.min(totalQ, currentIdx + 5)).map((q, i) => {
                  const absIdx = Math.max(0, currentIdx - 4) + i;
                  const ans = selectedAnswers[q.id];
                  const isThisCorrect = ans === q.answer;
                  return (
                    <button key={q.id} onClick={() => setCurrentIdx(absIdx)}
                      style={{
                        width: 32, height: 32, borderRadius: 8, border: "1.5px solid",
                        cursor: "pointer", fontSize: 11, fontWeight: 700, transition: "all 0.15s",
                        background: absIdx === currentIdx ? "#1e40af" : ans ? (isThisCorrect ? "#dcfce7" : "#fee2e2") : "#fff",
                        borderColor: absIdx === currentIdx ? "#1e40af" : ans ? (isThisCorrect ? "#16a34a" : "#dc2626") : "#e2e8f0",
                        color: absIdx === currentIdx ? "#fff" : ans ? (isThisCorrect ? "#166534" : "#991b1b") : "#64748b",
                      }}>
                      {q.id}
                    </button>
                  );
                })}
              </div>

              <button className="q-nav-btn" disabled={currentIdx === totalQ - 1}
                onClick={() => setCurrentIdx(i => i + 1)}>›</button>
            </div>

            {/* Skip if no answer */}
            {!selected && (
              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button className="btn-ghost" style={{ fontSize: 13 }}
                  onClick={() => setCurrentIdx(i => Math.min(totalQ - 1, i + 1))}>
                  Skip question →
                </button>
              </div>
            )}
            {selected && currentIdx < totalQ - 1 && (
              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button className="btn-primary" onClick={() => setCurrentIdx(i => i + 1)}>
                  Next Question →
                </button>
              </div>
            )}
            {selected && currentIdx === totalQ - 1 && (
              <div style={{ textAlign: "center", marginTop: 20 }}>
                <button className="btn-primary" onClick={() => setQuizMode("results")}>
                  View Results →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── REVIEW MODE ── */}
        {quizMode === "review" && (
          <div>
            {filteredQuestions.map((q, idx) => {
              const ans = selectedAnswers[q.id];
              const isC = ans === q.answer;
              const open = showRationale[`review_${q.id}`];
              return (
                <div key={q.id} className="card" style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span className="badge" style={{ background: "#dbeafe", color: "#1e40af" }}>Q{q.id}</span>
                      {ans && (
                        <span className="badge" style={{
                          background: isC ? "#dcfce7" : "#fee2e2",
                          color: isC ? "#166534" : "#991b1b"
                        }}>{isC ? "✓ Correct" : "✗ Incorrect"}</span>
                      )}
                      {!ans && <span className="badge" style={{ background: "#f1f5f9", color: "#64748b" }}>Not attempted</span>}
                    </div>
                    <button className="btn-ghost" style={{ fontSize: 12, padding: "5px 12px", whiteSpace: "nowrap" }}
                      onClick={() => setShowRationale(prev => ({ ...prev, [`review_${q.id}`]: !prev[`review_${q.id}`] }))}>
                      {open ? "Hide ▲" : "Show Answer ▼"}
                    </button>
                  </div>
                  <p style={{ fontSize: 14.5, color: "#1e293b", lineHeight: 1.6, marginBottom: open ? 14 : 0 }}>
                    {q.question}
                  </p>
                  {open && (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                        {(["A","B","C","D"]).map(optKey => (
                          <div key={optKey} style={{
                            padding: "10px 14px", borderRadius: 8, fontSize: 13.5, display: "flex", gap: 10,
                            background: optKey === q.answer ? "#dcfce7" : "#f8fafc",
                            border: `1.5px solid ${optKey === q.answer ? "#16a34a" : "#e2e8f0"}`,
                            color: optKey === q.answer ? "#166534" : "#475569",
                          }}>
                            <strong>{optKey}.</strong> {q.options[optKey]}
                            {optKey === q.answer && <span style={{ marginLeft: "auto" }}>✓</span>}
                          </div>
                        ))}
                      </div>
                      <div className="rationale-box">
                        <div style={{ fontWeight: 700, marginBottom: 5, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1e40af" }}>
                          📘 Rationale
                        </div>
                        {q.rationale}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── RESULTS MODE ── */}
        {quizMode === "results" && (
          <div>
            {/* Score Summary */}
            <div className="card" style={{ marginBottom: 24, textAlign: "center" }}>
              <div className="serif" style={{ fontSize: 26, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
                Your Results
              </div>
              <div style={{ fontSize: 56, fontWeight: 800, color: scoreColor, marginBottom: 8, fontFamily: "Georgia, serif" }}>
                {answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0}%
              </div>
              <div style={{ fontSize: 15, color: "#64748b", marginBottom: 20 }}>
                {correctCount} correct out of {answeredCount} answered ({totalQ - answeredCount} skipped)
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <div className="badge" style={{ background: "#dcfce7", color: "#166534", padding: "8px 16px", fontSize: 13 }}>
                  ✓ {correctCount} Correct
                </div>
                <div className="badge" style={{ background: "#fee2e2", color: "#991b1b", padding: "8px 16px", fontSize: 13 }}>
                  ✗ {answeredCount - correctCount} Incorrect
                </div>
                <div className="badge" style={{ background: "#f1f5f9", color: "#64748b", padding: "8px 16px", fontSize: 13 }}>
                  ○ {totalQ - answeredCount} Skipped
                </div>
              </div>
              {answeredCount > 0 && (
                <div style={{ marginTop: 16, fontSize: 14, fontWeight: 600, color: scoreColor }}>
                  {correctCount / answeredCount >= 0.8
                    ? "🎉 Excellent! You're well prepared for this element."
                    : correctCount / answeredCount >= 0.6
                    ? "📚 Good effort! Review the missed questions to solidify your knowledge."
                    : "💪 Keep studying! Focus on the rationales for incorrect answers."}
                </div>
              )}
              <div style={{ marginTop: 20 }}>
                <button className="btn-primary" onClick={resetQuiz} style={{ marginRight: 10 }}>Start Over</button>
                <button className="btn-secondary" onClick={() => setQuizMode("review")}>Review All Answers</button>
              </div>
            </div>

            {/* Incorrect questions list */}
            {answeredCount > 0 && (
              <div>
                <div className="serif" style={{ fontSize: 18, fontWeight: 600, color: "#0f172a", marginBottom: 14 }}>
                  Missed Questions — Review These
                </div>
                {filteredQuestions.filter(q => {
                  const ans = selectedAnswers[q.id];
                  return ans && ans !== q.answer;
                }).map(q => (
                  <div key={q.id} className="result-item"
                    onClick={() => { setCurrentIdx(filteredQuestions.indexOf(q)); setQuizMode("practice"); }}>
                    <div className="dot-incorrect" style={{ marginTop: 5 }} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b", marginBottom: 4 }}>
                        Q{q.id} — Your answer: {selectedAnswers[q.id]}. Correct: {q.answer}.
                      </div>
                      <div style={{ fontSize: 13.5, color: "#334155", lineHeight: 1.5 }}>{q.question}</div>
                    </div>
                  </div>
                ))}
                {filteredQuestions.filter(q => selectedAnswers[q.id] && selectedAnswers[q.id] !== q.answer).length === 0 && (
                  <div style={{ textAlign: "center", padding: 30, color: "#64748b", fontSize: 15 }}>
                    🌟 No missed questions! Perfect score on answered questions.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
